import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Upload, Palette, RotateCcw, AlertTriangle, Wand2 } from "lucide-react";
import { BrandPreview } from "@/components/admin/BrandPreview";
import { contrastRatio, suggestAccessible } from "@/lib/contrast";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";

const HEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

// Platform-admin branding editor for ONE organization (logo + primary colour).
export function OrgBrandingEditor({ orgId, orgName }) {
  const base = `/superadmin/orgs/${orgId}/branding`;
  const [primary, setPrimary] = useState("#10B981");
  const [name, setName] = useState("");
  const [logoData, setLogoData] = useState(null);
  const [logoUrl, setLogoUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const hydrate = (data) => { setPrimary(data.brand_primary || "#10B981"); setName(data.brand_name || ""); setLogoUrl(data.brand_logo_url || ""); setLogoData(null); };

  useEffect(() => {
    let on = true;
    setLoading(true);
    api.get(base).then(({ data }) => { if (on) hydrate(data); }).finally(() => { if (on) setLoading(false); });
    return () => { on = false; };
  }, [base]);

  const pickLogo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error("Logo must be under 2 MB"); return; }
    const r = new FileReader();
    r.onload = () => setLogoData(r.result);
    r.readAsDataURL(f);
  };

  const doSave = async (colorOverride) => {
    const col = colorOverride || primary;
    setBusy(true);
    try {
      const { data } = await api.patch(base, { brand_primary: col, brand_name: name || null, logo_base64: logoData });
      hydrate(data);
      toast.success(`Branding saved for ${orgName || "the organization"}`);
      window.dispatchEvent(new Event("branding-updated"));
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save branding"); }
    finally { setBusy(false); }
  };

  const save = () => {
    if (!HEX.test(primary)) { toast.error("Enter a valid hex colour like #10B981"); return; }
    if (contrastRatio(primary, "#ffffff") < 4.5) { setConfirmOpen(true); return; }
    doSave();
  };

  const reset = async () => {
    setResetting(true);
    try {
      const { data } = await api.delete(base);
      hydrate(data);
      toast.success("Reset to HireStream default");
      window.dispatchEvent(new Event("branding-updated"));
    } catch (e) { toast.error(e.response?.data?.detail || "Could not reset"); }
    finally { setResetting(false); }
  };

  const logoSrc = logoData || logoUrl || null;
  const validHex = HEX.test(primary);
  const lowContrast = validHex && contrastRatio(primary, "#ffffff") < 4.5;
  const safeShade = lowContrast ? suggestAccessible(primary) : null;

  return (
    <div data-testid="org-branding-editor">
      <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5"><Palette className="h-3.5 w-3.5" /> Branding</div>
      <div className="rounded-lg border border-border bg-card p-3 space-y-3">
        <BrandPreview primary={primary} name={name} logo={logoSrc} />
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-lg border border-border flex items-center justify-center overflow-hidden shrink-0" style={{ background: HEX.test(primary) ? `${primary}1f` : undefined }}>
            {logoSrc ? <img src={logoSrc} alt="logo preview" className="h-full w-full object-contain" data-testid="branding-logo-preview" /> : <span className="text-[10px] text-muted-foreground">No logo</span>}
          </div>
          <div className="flex-1 min-w-0 space-y-1">
            <Label className="text-xs">Logo (PNG/JPG/WEBP, under 2MB)</Label>
            <Input type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={pickLogo} data-testid="branding-logo-input" className="h-9 text-xs" />
          </div>
        </div>
        <div className="grid grid-cols-[auto_1fr] items-end gap-3">
          <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(primary) ? primary : "#10B981"} onChange={(e) => setPrimary(e.target.value)} data-testid="branding-color-swatch" className="h-9 w-12 rounded border border-border bg-transparent cursor-pointer" aria-label="Brand colour" />
          <div className="space-y-1"><Label className="text-xs">Brand colour</Label><Input value={primary} onChange={(e) => setPrimary(e.target.value)} data-testid="branding-color-hex" className="h-9 font-mono text-xs" /></div>
        </div>
        {lowContrast && (
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-2.5 flex items-start gap-2" data-testid="branding-contrast-warning">
            <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
            <div className="flex-1 space-y-1.5">
              <p className="text-xs text-amber-200 leading-relaxed">This colour may be hard to read with white button text. Buttons will use dark text, or pick a deeper shade for a crisp look.</p>
              <button type="button" onClick={() => setPrimary(safeShade)} className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-200 hover:text-amber-100" data-testid="branding-use-safe-shade">
                <Wand2 className="h-3.5 w-3.5" /> Use safer shade <span className="font-mono">{safeShade}</span>
              </button>
            </div>
          </div>
        )}
        <div className="space-y-1"><Label className="text-xs">Display name (optional)</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="HireStream" data-testid="branding-name-input" className="h-9 text-sm" /></div>
        <div className="flex gap-2">
          <Button size="sm" onClick={save} disabled={busy || loading} className="flex-1 gap-1.5" data-testid="branding-save">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Save branding</Button>
          <Button size="sm" variant="outline" onClick={reset} disabled={resetting || loading} className="gap-1.5" data-testid="branding-reset">{resetting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />} Reset</Button>
        </div>
        <p className="text-[11px] text-muted-foreground">Applies to this org's recruiter workspace and public careers pages. Advisory colours stay readable automatically.</p>
      </div>
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="contrast-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>This colour may be hard to read</AlertDialogTitle>
            <AlertDialogDescription>
              White text on <span className="font-mono">{primary}</span> is below the recommended readability level. We suggest the deeper shade <span className="font-mono">{safeShade}</span> for crisp buttons and links.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel data-testid="contrast-cancel">Keep editing</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setPrimary(safeShade); doSave(safeShade); }} data-testid="contrast-use-safe">Use safer shade &amp; save</AlertDialogAction>
            <AlertDialogAction onClick={() => doSave()} className="bg-muted text-foreground hover:bg-muted/80" data-testid="contrast-save-anyway">Save anyway</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
